/**
 * Phase 5 FE — Subject Detail "Adverse Events" section (AC1 entry surface).
 *
 * Lists the subject's AEs with columns: reference, description, start
 * date, status badge (colour-coded), SAE flag icon. "+ Log AE" button
 * opens a modal that captures every AC1 required field, with grade and
 * relationship dropdowns populated from the study's configured
 * options (AC2). When Serious=Yes the SAE reporting fields become
 * required (AC4). Row click opens a status-update flow.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ClipboardPlus,
  History,
  Loader2,
  ShieldAlert,
  X,
} from 'lucide-react';
import {
  adverseEvents,
  studyAeConfig,
  type AdverseEventCurrentStatus,
  type AdverseEventCreateBody,
  type AdverseEventRow,
} from '@/api/client';
import { fmtDate } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import { errorText } from '@/lib/permissionError';

const STATUS_LABELS: Record<AdverseEventCurrentStatus, string> = {
  ongoing: 'Ongoing',
  resolved: 'Resolved',
  resolved_with_sequelae: 'Resolved (sequelae)',
  condition_worsening: 'Worsening',
};

const STATUS_BADGE: Record<AdverseEventCurrentStatus, string> = {
  ongoing: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  resolved_with_sequelae: 'bg-teal-50 text-teal-700 border-teal-200',
  condition_worsening: 'bg-danger-50 text-danger-700 border-danger-200',
};

const LOG_ROLES = new Set([
  'platform_admin',
  'application_admin',
  'site_admin',
  'coordinator',
  'principal_investigator',
  'sub_investigator',
]);

export function AdverseEventsSection({
  subjectId,
  studyId,
}: {
  subjectId: string;
  studyId: string;
}) {
  const { user } = useAuth();
  const [logOpen, setLogOpen] = useState(false);
  const [detailAe, setDetailAe] = useState<AdverseEventRow | null>(null);

  const listQ = useQuery({
    queryKey: ['ae', 'list', subjectId],
    queryFn: () => adverseEvents.list({ subject_id: subjectId, limit: 200 }),
    enabled: !!subjectId,
  });

  const canLog = !!user && LOG_ROLES.has(user.role);
  const rows = listQ.data?.rows ?? [];

  return (
    <section className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-brand-500" />
          <div>
            <h2 className="text-sm font-semibold text-ink-primary">Adverse events</h2>
            <p className="text-xs text-ink-muted">
              Safety events logged for this subject. SAEs are highlighted for review.
            </p>
          </div>
        </div>
        {canLog && (
          <button
            type="button"
            onClick={() => setLogOpen(true)}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
          >
            <ClipboardPlus className="mr-1 inline h-3 w-3" />
            Log AE
          </button>
        )}
      </div>

      {listQ.isLoading ? (
        <div className="flex items-center gap-1.5 text-xs text-ink-muted">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-ink-muted">No adverse events logged.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-surface-muted/40 text-left">
            <tr>
              <th className="px-2 py-1.5 font-semibold">Ref</th>
              <th className="px-2 py-1.5 font-semibold">Description</th>
              <th className="px-2 py-1.5 font-semibold">Start</th>
              <th className="px-2 py-1.5 font-semibold">Grade</th>
              <th className="px-2 py-1.5 font-semibold">Status</th>
              <th className="px-2 py-1.5 font-semibold text-center">SAE</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {rows.map((row) => (
              <tr
                key={row.ae_id}
                onClick={() => setDetailAe(row)}
                className={`cursor-pointer hover:bg-surface-muted/40 ${row.is_serious ? 'bg-danger-50/30' : ''}`}
              >
                <td className="px-2 py-1.5 font-mono text-[11px] text-ink-secondary">{row.reference_number}</td>
                <td className="max-w-md truncate px-2 py-1.5" title={row.description}>{row.description}</td>
                <td className="px-2 py-1.5 text-ink-muted">{fmtDate(row.start_date)}</td>
                <td className="px-2 py-1.5">{row.grade}</td>
                <td className="px-2 py-1.5">
                  <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${STATUS_BADGE[row.current_status]}`}>
                    {STATUS_LABELS[row.current_status]}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-center">
                  {row.is_serious && (
                    <AlertTriangle
                      className="mx-auto h-3.5 w-3.5 text-danger-600"
                      aria-label="SAE"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {logOpen && (
        <LogAdverseEventDialog
          subjectId={subjectId}
          studyId={studyId}
          onClose={() => setLogOpen(false)}
        />
      )}

      {detailAe && (
        <AdverseEventDetailDrawer
          aeId={detailAe.ae_id}
          onClose={() => setDetailAe(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function LogAdverseEventDialog({
  subjectId,
  studyId,
  onClose,
}: {
  subjectId: string;
  studyId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const configQ = useQuery({
    queryKey: ['study', studyId, 'ae-config'],
    queryFn: () => studyAeConfig.get(studyId),
    staleTime: 60_000,
  });

  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [stopDate, setStopDate] = useState('');
  const [isSerious, setIsSerious] = useState(false);
  const [grade, setGrade] = useState('');
  const [relationship, setRelationship] = useState('');
  const [isAnticipated, setIsAnticipated] = useState(false);
  const [actionTaken, setActionTaken] = useState<AdverseEventCreateBody['action_taken']>('none');
  const [otherActions, setOtherActions] = useState('');
  const [reportedToSponsor, setReportedToSponsor] = useState(false);
  const [sponsorDate, setSponsorDate] = useState('');
  const [reportedToIrb, setReportedToIrb] = useState(false);
  const [irbDate, setIrbDate] = useState('');
  const [piInitials, setPiInitials] = useState('');
  const [piSignedDate, setPiSignedDate] = useState(new Date().toISOString().slice(0, 10));
  const [saeCategory, setSaeCategory] = useState('');
  const [error, setError] = useState<string | null>(null);

  const config = configQ.data?.config;

  const create = useMutation({
    mutationFn: () => {
      const body: AdverseEventCreateBody = {
        subject_id: subjectId,
        description: description.trim(),
        start_date: startDate,
        stop_date: stopDate || undefined,
        is_serious: isSerious,
        grade,
        relationship,
        is_anticipated: isAnticipated,
        action_taken: actionTaken,
        other_actions: actionTaken === 'other' ? otherActions.trim() || undefined : undefined,
        reported_to_sponsor: reportedToSponsor,
        reported_to_sponsor_date: reportedToSponsor && sponsorDate ? sponsorDate : undefined,
        reported_to_irb: reportedToIrb,
        reported_to_irb_date: reportedToIrb && irbDate ? irbDate : undefined,
        pi_initials: piInitials.trim(),
        pi_signed_date: piSignedDate,
        sae_category: isSerious && saeCategory ? saeCategory : undefined,
      };
      return adverseEvents.create(body);
    },
    onSuccess: (r) => {
      toast.success(`Adverse event ${r.reference_number} logged.`);
      qc.invalidateQueries({ queryKey: ['ae', 'list', subjectId] });
      onClose();
    },
    onError: (e) => {
      const msg = errorText(e, 'Failed to log AE');
      setError(msg);
      toast.error(msg);
    },
  });

  function validate(): string | null {
    if (!description.trim()) return 'Description is required.';
    if (!startDate) return 'Start date is required.';
    if (!grade) return 'Grade is required.';
    if (!relationship) return 'Relationship is required.';
    if (!piInitials.trim()) return 'PI initials are required.';
    if (!piSignedDate) return 'PI signed date is required.';
    if (isSerious && (!reportedToSponsor || !reportedToIrb)) {
      return 'Serious AEs require sponsor + IRB reporting flags to be set.';
    }
    if (isSerious && reportedToSponsor && !sponsorDate) return 'Sponsor reporting date required for SAE.';
    if (isSerious && reportedToIrb && !irbDate) return 'IRB reporting date required for SAE.';
    if (actionTaken === 'other' && !otherActions.trim()) return 'Please describe the action taken.';
    return null;
  }

  function handleSubmit() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    create.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-ink-primary">
              <ClipboardPlus className="h-4 w-4 text-accent-700" />
              Log adverse event
            </h3>
            <p className="text-[11px] text-ink-muted">
              All fields marked * are required. When Serious = Yes, sponsor + IRB reporting fields become required.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {configQ.isLoading ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading study configuration…
          </div>
        ) : (
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Description *</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={2000}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
                placeholder="Signs / symptoms or accepted medical terminology"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Start date *</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Stop date</span>
                <input
                  type="date"
                  value={stopDate}
                  onChange={(e) => setStopDate(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Grade *</span>
                <select
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                >
                  <option value="">— Select —</option>
                  {config?.grading_options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Relationship *</span>
                <select
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                >
                  <option value="">— Select —</option>
                  {config?.relationship_options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={isSerious}
                  onChange={(e) => setIsSerious(e.target.checked)}
                />
                <span className="font-medium text-ink-secondary">Serious (SAE)</span>
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={isAnticipated}
                  onChange={(e) => setIsAnticipated(e.target.checked)}
                />
                <span className="font-medium text-ink-secondary">Anticipated</span>
              </label>
            </div>

            {isSerious && (
              <div className="rounded border border-danger-200 bg-danger-50/40 p-3">
                <p className="mb-2 text-xs font-semibold text-danger-800">
                  <AlertTriangle className="mr-1 inline h-3 w-3" />
                  SAE reporting (required)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs">
                    <span className="font-medium text-ink-secondary">Reported to sponsor *</span>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={reportedToSponsor}
                        onChange={(e) => setReportedToSponsor(e.target.checked)}
                      />
                      <input
                        type="date"
                        value={sponsorDate}
                        onChange={(e) => setSponsorDate(e.target.value)}
                        disabled={!reportedToSponsor}
                        className="flex-1 rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-50"
                      />
                    </div>
                  </label>
                  <label className="block text-xs">
                    <span className="font-medium text-ink-secondary">Reported to IRB *</span>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={reportedToIrb}
                        onChange={(e) => setReportedToIrb(e.target.checked)}
                      />
                      <input
                        type="date"
                        value={irbDate}
                        onChange={(e) => setIrbDate(e.target.value)}
                        disabled={!reportedToIrb}
                        className="flex-1 rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-50"
                      />
                    </div>
                  </label>
                </div>
                {config?.sae_categories && config.sae_categories.length > 0 && (
                  <label className="mt-3 block text-xs">
                    <span className="font-medium text-ink-secondary">SAE category</span>
                    <select
                      value={saeCategory}
                      onChange={(e) => setSaeCategory(e.target.value)}
                      className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                    >
                      <option value="">— Select —</option>
                      {config.sae_categories.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Action taken with study drug / device</span>
              <select
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value as typeof actionTaken)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
              >
                <option value="none">None</option>
                <option value="reduced">Reduced</option>
                <option value="interrupted">Interrupted</option>
                <option value="discontinued">Discontinued</option>
                <option value="other">Other</option>
              </select>
            </label>
            {actionTaken === 'other' && (
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Other actions description *</span>
                <textarea
                  value={otherActions}
                  onChange={(e) => setOtherActions(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
                />
              </label>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">PI initials *</span>
                <input
                  type="text"
                  value={piInitials}
                  onChange={(e) => setPiInitials(e.target.value)}
                  maxLength={20}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">PI signed date *</span>
                <input
                  type="date"
                  value={piSignedDate}
                  onChange={(e) => setPiSignedDate(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
            </div>

            {error && (
              <p className="text-xs text-danger-600">{error}</p>
            )}
          </div>
        )}

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={create.isPending || configQ.isLoading}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {create.isPending ? 'Logging…' : 'Log AE'}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AdverseEventDetailDrawer({
  aeId,
  onClose,
}: {
  aeId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['ae', 'detail', aeId],
    queryFn: () => adverseEvents.detail(aeId),
  });
  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const ae = detailQ.data?.adverse_event;
  const history = detailQ.data?.status_history ?? [];

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 !m-0"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[92vh] w-full max-w-lg flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-start justify-between border-b border-surface-border px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-primary">
              {ae ? ae.reference_number : 'Loading…'}
              {ae?.is_serious && (
                <AlertTriangle className="ml-1.5 inline h-3.5 w-3.5 text-danger-600" aria-label="SAE" />
              )}
            </h3>
            <p className="mt-0.5 truncate text-[11px] text-ink-muted">
              {ae?.description ?? ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {detailQ.isLoading || !ae ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-xs">
            <FieldGrid
              rows={[
                ['Grade', ae.grade],
                ['Relationship', ae.relationship],
                ['Status', STATUS_LABELS[ae.current_status]],
                ['Serious', ae.is_serious ? 'Yes' : 'No'],
                ['Anticipated', ae.is_anticipated ? 'Yes' : 'No'],
                ['Start', fmtDate(ae.start_date)],
                ['Stop', ae.stop_date ? fmtDate(ae.stop_date) : '—'],
                ['Action taken', ae.action_taken.replace(/_/g, ' ')],
                ['Reported to sponsor', ae.reported_to_sponsor ? (ae.reported_to_sponsor_date ? fmtDate(ae.reported_to_sponsor_date) : 'Yes') : 'No'],
                ['Reported to IRB', ae.reported_to_irb ? (ae.reported_to_irb_date ? fmtDate(ae.reported_to_irb_date) : 'Yes') : 'No'],
                ['PI initials', ae.pi_initials],
                ['PI signed', fmtDate(ae.pi_signed_date)],
              ]}
            />

            <div>
              <h4 className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold text-ink-primary">
                <History className="h-3 w-3" /> Status history
              </h4>
              {history.length === 0 ? (
                <p className="text-[11px] text-ink-muted">No transitions yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {history.map((h) => (
                    <li key={h.history_id} className="rounded border border-surface-border bg-surface-muted/30 p-2">
                      <div className="flex items-center justify-between text-[10px] text-ink-muted">
                        <span>
                          {h.from_status
                            ? `${STATUS_LABELS[h.from_status]} → ${STATUS_LABELS[h.to_status]}`
                            : `Initial: ${STATUS_LABELS[h.to_status]}`}
                        </span>
                        <span>{new Date(h.changed_at).toLocaleString()}</span>
                      </div>
                      <div className="mt-1 text-[11px] text-ink-primary">{h.reason_notes}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          {ae && (
            <>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => setStatusOpen(true)}
                className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
              >
                Update status
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700"
          >
            Close
          </button>
        </footer>

        {statusOpen && ae && (
          <UpdateStatusInline
            aeId={aeId}
            currentStatus={ae.current_status}
            onClose={() => setStatusOpen(false)}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['ae', 'detail', aeId] });
              qc.invalidateQueries({ queryKey: ['ae', 'list', ae.subject_id] });
              setStatusOpen(false);
            }}
          />
        )}

        {editOpen && ae && (
          <EditAdverseEventInline
            ae={ae}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              qc.invalidateQueries({ queryKey: ['ae', 'detail', aeId] });
              qc.invalidateQueries({ queryKey: ['ae', 'list', ae.subject_id] });
              setEditOpen(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Compact edit modal — patches non-status fields on an existing AE.
 * BE's update handler already computes a field-level diff and rejects
 * a no-op patch, so this dialog can send every field and let the
 * server figure out what changed.
 *
 * Fields exposed match the BE schema: description, dates, is_serious
 * (SAE promotion), grade, relationship, is_anticipated, action_taken,
 * other_actions, sponsor/IRB reporting flags + dates, PI signoff,
 * sae_category. Grade / relationship / SAE category options load from
 * studyAeConfig so the edit form stays consistent with the log form.
 */
function EditAdverseEventInline({
  ae,
  onClose,
  onSaved,
}: {
  ae: AdverseEventRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const configQ = useQuery({
    queryKey: ['study', ae.study_id, 'ae-config'],
    queryFn: () => studyAeConfig.get(ae.study_id),
    staleTime: 60_000,
  });

  const [description, setDescription] = useState(ae.description);
  const [startDate, setStartDate] = useState(ae.start_date);
  const [stopDate, setStopDate] = useState(ae.stop_date ?? '');
  const [isSerious, setIsSerious] = useState(ae.is_serious);
  const [grade, setGrade] = useState(ae.grade);
  const [relationship, setRelationship] = useState(ae.relationship);
  const [isAnticipated, setIsAnticipated] = useState(ae.is_anticipated);
  const [actionTaken, setActionTaken] = useState(ae.action_taken);
  const [otherActions, setOtherActions] = useState(ae.other_actions ?? '');
  const [reportedToSponsor, setReportedToSponsor] = useState(ae.reported_to_sponsor);
  const [sponsorDate, setSponsorDate] = useState(ae.reported_to_sponsor_date ?? '');
  const [reportedToIrb, setReportedToIrb] = useState(ae.reported_to_irb);
  const [irbDate, setIrbDate] = useState(ae.reported_to_irb_date ?? '');
  const [piInitials, setPiInitials] = useState(ae.pi_initials);
  const [piSignedDate, setPiSignedDate] = useState(ae.pi_signed_date);
  const [saeCategory, setSaeCategory] = useState(ae.sae_category ?? '');
  const [error, setError] = useState<string | null>(null);

  const config = configQ.data?.config;

  const save = useMutation({
    mutationFn: () =>
      adverseEvents.update(ae.ae_id, {
        description: description.trim(),
        start_date: startDate,
        stop_date: stopDate || undefined,
        is_serious: isSerious,
        grade,
        relationship,
        is_anticipated: isAnticipated,
        action_taken: actionTaken,
        other_actions: actionTaken === 'other' ? otherActions.trim() || undefined : undefined,
        reported_to_sponsor: reportedToSponsor,
        reported_to_sponsor_date: reportedToSponsor && sponsorDate ? sponsorDate : undefined,
        reported_to_irb: reportedToIrb,
        reported_to_irb_date: reportedToIrb && irbDate ? irbDate : undefined,
        pi_initials: piInitials.trim(),
        pi_signed_date: piSignedDate,
        sae_category: isSerious && saeCategory ? saeCategory : undefined,
      }),
    onSuccess: () => {
      toast.success('Adverse event updated.');
      onSaved();
    },
    onError: (e) => {
      const msg = errorText(e, 'Failed to save');
      setError(msg);
      toast.error(msg);
    },
  });

  function validate(): string | null {
    if (!description.trim()) return 'Description is required.';
    if (!startDate) return 'Start date is required.';
    if (!grade) return 'Grade is required.';
    if (!relationship) return 'Relationship is required.';
    if (!piInitials.trim()) return 'PI initials are required.';
    if (!piSignedDate) return 'PI signed date is required.';
    if (isSerious && (!reportedToSponsor || !reportedToIrb)) {
      return 'Serious AEs require sponsor + IRB reporting flags to be set.';
    }
    if (isSerious && reportedToSponsor && !sponsorDate) return 'Sponsor reporting date required for SAE.';
    if (isSerious && reportedToIrb && !irbDate) return 'IRB reporting date required for SAE.';
    if (actionTaken === 'other' && !otherActions.trim()) return 'Please describe the action taken.';
    return null;
  }

  function handleSubmit() {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    save.mutate();
  }

  return (
    <div className="absolute inset-0 z-[10] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col rounded-lg border border-surface-border bg-surface-card shadow-xl">
        <header className="flex items-center justify-between border-b border-surface-border px-4 py-3">
          <div>
            <h4 className="text-sm font-semibold text-ink-primary">Edit {ae.reference_number}</h4>
            <p className="text-[11px] text-ink-muted">
              Correct data-entry mistakes or record new sponsor / IRB reporting dates.
              Status transitions still go through "Update status".
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-ink-muted hover:bg-surface-muted/50"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {configQ.isLoading ? (
          <div className="p-6 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading configuration…
          </div>
        ) : (
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Description *</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={2000}
                className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Start date *</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Stop date</span>
                <input
                  type="date"
                  value={stopDate}
                  onChange={(e) => setStopDate(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Grade *</span>
                <select
                  value={grade}
                  onChange={(e) => setGrade(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                >
                  {config?.grading_options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                  {config && !config.grading_options.some((o) => o.value === grade) && grade && (
                    <option key={grade} value={grade}>{grade} (legacy)</option>
                  )}
                </select>
              </label>
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Relationship *</span>
                <select
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                >
                  {config?.relationship_options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                  {config && !config.relationship_options.some((o) => o.value === relationship) && relationship && (
                    <option key={relationship} value={relationship}>{relationship} (legacy)</option>
                  )}
                </select>
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={isSerious}
                  onChange={(e) => setIsSerious(e.target.checked)}
                />
                <span className="font-medium text-ink-secondary">Serious (SAE)</span>
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={isAnticipated}
                  onChange={(e) => setIsAnticipated(e.target.checked)}
                />
                <span className="font-medium text-ink-secondary">Anticipated</span>
              </label>
            </div>

            {isSerious && (
              <div className="rounded border border-danger-200 bg-danger-50/40 p-3">
                <p className="mb-2 text-xs font-semibold text-danger-800">
                  <AlertTriangle className="mr-1 inline h-3 w-3" /> SAE reporting (required)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block text-xs">
                    <span className="font-medium text-ink-secondary">Reported to sponsor</span>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={reportedToSponsor}
                        onChange={(e) => setReportedToSponsor(e.target.checked)}
                      />
                      <input
                        type="date"
                        value={sponsorDate}
                        onChange={(e) => setSponsorDate(e.target.value)}
                        disabled={!reportedToSponsor}
                        className="flex-1 rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-50"
                      />
                    </div>
                  </label>
                  <label className="block text-xs">
                    <span className="font-medium text-ink-secondary">Reported to IRB</span>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={reportedToIrb}
                        onChange={(e) => setReportedToIrb(e.target.checked)}
                      />
                      <input
                        type="date"
                        value={irbDate}
                        onChange={(e) => setIrbDate(e.target.value)}
                        disabled={!reportedToIrb}
                        className="flex-1 rounded border border-surface-border px-2 py-1 text-xs disabled:opacity-50"
                      />
                    </div>
                  </label>
                </div>
                {config?.sae_categories && config.sae_categories.length > 0 && (
                  <label className="mt-3 block text-xs">
                    <span className="font-medium text-ink-secondary">SAE category</span>
                    <select
                      value={saeCategory}
                      onChange={(e) => setSaeCategory(e.target.value)}
                      className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                    >
                      <option value="">— Select —</option>
                      {config.sae_categories.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            )}

            <label className="block text-xs">
              <span className="font-medium text-ink-secondary">Action taken</span>
              <select
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value as typeof actionTaken)}
                className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
              >
                <option value="none">None</option>
                <option value="reduced">Reduced</option>
                <option value="interrupted">Interrupted</option>
                <option value="discontinued">Discontinued</option>
                <option value="other">Other</option>
              </select>
            </label>
            {actionTaken === 'other' && (
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">Other actions description *</span>
                <textarea
                  value={otherActions}
                  onChange={(e) => setOtherActions(e.target.value)}
                  rows={2}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
                />
              </label>
            )}

            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">PI initials *</span>
                <input
                  type="text"
                  value={piInitials}
                  onChange={(e) => setPiInitials(e.target.value)}
                  maxLength={20}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
              <label className="block text-xs">
                <span className="font-medium text-ink-secondary">PI signed date *</span>
                <input
                  type="date"
                  value={piSignedDate}
                  onChange={(e) => setPiSignedDate(e.target.value)}
                  className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
                />
              </label>
            </div>

            {error && <p className="text-xs text-danger-600">{error}</p>}
          </div>
        )}

        <footer className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={save.isPending || configQ.isLoading}
            onClick={handleSubmit}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function FieldGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5">
      {rows.map(([k, v]) => (
        <div key={k} className="rounded border border-surface-border bg-surface-muted/20 px-2 py-1">
          <dt className="text-[10px] uppercase tracking-wider text-ink-muted">{k}</dt>
          <dd className="text-xs text-ink-primary">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function UpdateStatusInline({
  aeId,
  currentStatus,
  onClose,
  onSaved,
}: {
  aeId: string;
  currentStatus: AdverseEventCurrentStatus;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [toStatus, setToStatus] = useState<AdverseEventCurrentStatus>('resolved');
  const [reason, setReason] = useState('');
  const options = useMemo(
    () =>
      (['ongoing', 'resolved', 'resolved_with_sequelae', 'condition_worsening'] as AdverseEventCurrentStatus[])
        .filter((s) => s !== currentStatus),
    [currentStatus],
  );

  const update = useMutation({
    mutationFn: () =>
      adverseEvents.updateStatus(aeId, { to_status: toStatus, reason_notes: reason.trim() }),
    onSuccess: () => {
      toast.success('AE status updated.');
      onSaved();
    },
    onError: (e) => toast.error(errorText(e, 'Failed')),
  });

  return (
    <div className="absolute inset-0 z-[10] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-lg border border-surface-border bg-surface-card p-4 shadow-xl">
        <h4 className="text-sm font-semibold text-ink-primary">Update AE status</h4>
        <p className="mt-1 text-[11px] text-ink-muted">
          Current: <strong>{STATUS_LABELS[currentStatus]}</strong>
        </p>
        <label className="mt-3 block text-xs">
          <span className="font-medium text-ink-secondary">New status</span>
          <select
            value={toStatus}
            onChange={(e) => setToStatus(e.target.value as AdverseEventCurrentStatus)}
            className="mt-1 w-full rounded border border-surface-border px-3 py-1.5 text-sm"
          >
            {options.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </label>
        <label className="mt-3 block text-xs">
          <span className="font-medium text-ink-secondary">Reason / notes *</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
          />
        </label>
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!reason.trim() || update.isPending}
            onClick={() => update.mutate()}
            className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
